<template>
  <div class="mb-4 category-item-crud">
    <a class="btn text-themed go-back" @click="goBack()">
      {{ $t("flexben.general.go_back") }}
    </a>

    <div>
      <div class="row px-3 mb-2">
        <div class="col-12 col-md-6 pl-0 d-flex align-self-center flexben-page-title">
          <h1 class="m-0">
            {{ (formAction === 'create') ? $t("flexben.categories.new") : $t("flexben.categories.edit") }}
          </h1>
        </div>

        <div class="col-12 col-md-6 my-2 pr-0">
          <div class="float-right">
            <div class="float-right">
              <button
                v-if="formAction === 'update'"
                class="btn mx-1 text-danger delete_btn"
                type="button"
                @click="onDeleteCategory"
              >
                {{ $t("flexben.categories.delete") }}
              </button>

              <button class="btn ml-1 save-btn" type="button" @click="submitForm">
                {{ $t("flexben.categories.save") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="background-white">
        <div class="row p-3">
          <div class="col-12">
            <h3 class="mt-0 mb-3">{{ $t("flexben.benefit.details") }}</h3>
          </div>

          <!-- LEFT COLUMN -->
          <div class="col-12 col-lg-4 mb-4 mb-lg-0">
            <div class="upload-card primary-upload-card">
              <div class="upload-card-header">
                <div>
                  <h4 class="upload-title mb-1">Icon</h4>
                  <p class="upload-subtitle m-0">Obligatoriu</p>
                </div>
                <span class="required-badge">Required</span>
              </div>

              <div class="upload-card-body">
                <label class="label-input w-100 mb-2">Icon categorie</label>
                <vue2Dropzone
                  ref="iconUrlRef"
                  id="iconUrl"
                  :options="dropzoneOptions.icon"
                />
                <div v-if="errors.icon_url" class="text-danger mt-2">
                  {{ errors.icon_url }}
                </div>
              </div>
            </div>

            <div class="upload-card optional-upload-card mt-3">
              <button
                class="optional-toggle"
                type="button"
                @click="showOptionalMedia = !showOptionalMedia"
              >
                <span>Media opțională</span>
                <span>{{ showOptionalMedia ? '−' : '+' }}</span>
              </button>

              <transition name="fade">
                <div v-if="showOptionalMedia" class="upload-card-body pt-3">
                  <div class="optional-media-block mb-4">
                    <label class="label-input w-100 mb-2">Image</label>
                    <vue2Dropzone
                      ref="imageUrlRef"
                      id="imageUrl"
                      :options="dropzoneOptions.image"
                    />
                  </div>

                  <div class="optional-media-block">
                    <label class="label-input w-100 mb-2">Cover</label>
                    <vue2Dropzone
                      ref="coverUrlRef"
                      id="coverUrl"
                      :options="dropzoneOptions.cover"
                    />
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- RIGHT COLUMN -->
          <div class="col-12 col-lg-8">
            <div class="row">
              <!-- START / END DATE -->
              <div class="w-100">
                <div class="col-12 col-md-6 form-group">
                  <label for="start_date" class="col-form-label pt-0">
                    Start date
                  </label>
                  <input
                    id="start_date"
                    type="datetime-local"
                    class="form-control"
                    v-model="formData.start_date"
                  >
                  <div v-if="errors.start_date" class="text-danger">
                    {{ errors.start_date }}
                  </div>
                </div>

                <div class="col-12 col-md-6 form-group">
                  <label for="end_date" class="col-form-label pt-0">
                    End date
                  </label>
                  <input
                    id="end_date"
                    type="datetime-local"
                    class="form-control"
                    v-model="formData.end_date"
                  >
                  <div v-if="errors.end_date" class="text-danger">
                    {{ errors.end_date }}
                  </div>
                </div>
              </div>

              <!-- TRACKING -->
              <div class="w-100">
                <div class="col-12 form-group">
                  <label for="tracking" class="col-form-label pt-0">
                    Tracking mobile
                  </label>
                  <input
                    id="tracking"
                    type="text"
                    class="form-control"
                    v-model="formData.tracking"
                    placeholder="Ex: category_promo_spring"
                  >
                  <div v-if="errors.tracking" class="text-danger">
                    {{ errors.tracking }}
                  </div>
                </div>
              </div>

              <!-- TITLE -->
              <div class="w-100" v-if="formData.title">
                <div
                  class="col-12 col-md-6 form-group"
                  v-for="(obj, index) in formData.title"
                  :key="'title_' + index"
                >
                  <label :for="'title_' + index" class="form-fields-required col-form-label pt-0">
                    {{ $t("flexben.categories.fields.name") }} - {{ index }}
                  </label>
                  <input
                    :id="'title_' + index"
                    type="text"
                    class="form-control"
                    v-model="formData.title[index]"
                  >
                  <div v-if="errors.title && errors.title[index]" class="text-danger">
                    {{ errors.title[index] }}
                  </div>
                </div>
              </div>

              <!-- DESCRIPTION -->
              <div class="w-100" v-if="formData.description">
                <div
                  class="col-12 col-md-6 form-group"
                  v-for="(obj, index) in formData.description"
                  :key="'description_' + index"
                >
                  <label :for="'description_' + index" class="form-fields-required col-form-label pt-0">
                    {{ $t("flexben.categories.fields.description") }} - {{ index }}
                  </label>
                  <textarea
                    rows="4"
                    :id="'description_' + index"
                    class="form-control-textarea w-100 my-0"
                    v-model="formData.description[index]"
                  />
                  <div v-if="errors.description && errors.description[index]" class="text-danger">
                    {{ errors.description[index] }}
                  </div>
                </div>
              </div>

              <!-- PRODUCTS TABLE -->
              <div class="col-12 mt-4">
                <div class="products-section">
                  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                    <div>
                      <h4 class="m-0">Produse atașate</h4>
                      <small class="text-muted">Poți schimba ordinea prin drag & drop.</small>
                    </div>

                    <button class="btn save-btn mt-2 mt-md-0" type="button" @click="openProductModal">
                      Adaugă produs
                    </button>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-bordered table-hover products-table">
                      <thead>
                      <tr>
                        <th style="width: 60px;"></th>
                        <th style="width: 90px;">ID</th>
                        <th>Nume</th>
                        <th style="width: 90px;">Ordine</th>
                        <th style="width: 140px;">Acțiuni</th>
                      </tr>
                      </thead>

                      <tbody v-if="selectedProducts.length === 0">
                      <tr>
                        <td colspan="5" class="text-center text-muted py-4">
                          Nu există produse atașate.
                        </td>
                      </tr>
                      </tbody>

                      <draggable
                        v-else
                        v-model="selectedProducts"
                        tag="tbody"
                        handle=".drag-handle"
                        ghost-class="sortable-ghost"
                        @end="onProductsReorder"
                      >
                        <tr
                          v-for="(product, index) in selectedProducts"
                          :key="'product_' + product.id"
                        >
                          <td class="text-center align-middle">
                            <span class="drag-handle">☰</span>
                          </td>
                          <td class="align-middle">{{ product.id }}</td>
                          <td class="align-middle">{{ getProductLabel(product) }}</td>
                          <td class="align-middle">{{ index + 1 }}</td>
                          <td class="align-middle">
                            <button
                              type="button"
                              class="btn btn-sm btn-danger"
                              @click="removeProduct(index)"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      </draggable>
                    </table>
                  </div>

                  <div v-if="errors.products" class="text-danger mt-2">
                    {{ errors.products }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div><!-- /background-white -->
    </div>

    <BrandsSelectorModal
      v-if="showProductModal"
      :type="1"
      @close="showProductModal = false"
      @select="onProductSelected"
    />

    <content-loader
      :loader-visible="loading"
      :loader-error="loaderError"
    />
  </div>
</template>

<script>
import HttpCatServices from "@/superUp-module/resources/services/httpCatServices";
import vue2Dropzone from "vue2-dropzone";

import "vue2-dropzone/dist/vue2Dropzone.min.css";
import draggable from "vuedraggable";
import httpFlexbenService from "@/http/http-flexben";
import BrandsSelectorModal from "@/superUp-module/resources/views/layout/modals/BrandsSelectorModal.vue";

export default {
  name: "CategoryItemCrud",
  components: {
    BrandsSelectorModal,
    vue2Dropzone,
    draggable
  },
  props: {
    itemId: {
      type: Number,
      default: null
    },
    formAction: {
      type: String,
      default: null
    }
  },
  computed: {
    httpFlexbenService() {
      return httpFlexbenService;
    },
    dropzoneOptions() {
      return {
        icon: {
          url: "https://httpbin.org/post",
          height: 120,
          thumbnailWidth: 300,
          maxFiles: 1,
          addRemoveLinks: true,
          autoProcessQueue: false
        },
        image: {
          url: "https://httpbin.org/post",
          height: 220,
          thumbnailWidth: 220,
          maxFiles: 1,
          addRemoveLinks: true,
          autoProcessQueue: false
        },
        cover: {
          url: "https://httpbin.org/post",
          height: 280,
          thumbnailWidth: 280,
          maxFiles: 1,
          addRemoveLinks: true,
          autoProcessQueue: false
        }
      };
    }
  },
  data() {
    return {
      showProductModal: false,
      showOptionalMedia: false,
      loading: false,
      loaderError: false,
      selectedProducts: [],
      existingFiles: {
        icon_url: null,
        image_url: null,
        cover_url: null
      },
      formData: {
        name: null,
        title: {
          ro: null,
          en: null
        },
        description: {
          ro: null,
          en: null
        },
        isActive: false,
        image_url: null,
        cover_url: null,
        icon_url: null,
        start_date: null,
        end_date: null,
        tracking: null,
        products: []
      },
      errors: {
        title: {
          ro: null,
          en: null
        },
        description: {
          ro: null,
          en: null
        },
        start_date: null,
        end_date: null,
        tracking: null,
        products: null,
        icon_url: null
      }
    };
  },
  created() {
    this.loading = true;

    if (this.itemId !== null) {
      HttpCatServices.get(this.itemId)
        .then(result => {
          const data = result.data;

          this.formData.name = data.name || null;

          const parsedTitle = this.parseJsonField(data.title, { ro: null, en: null });
          this.formData.title.ro = parsedTitle.ro;
          this.formData.title.en = parsedTitle.en;

          const parsedDescription = this.parseJsonField(data.description, { ro: null, en: null });
          this.formData.description.ro = parsedDescription.ro;
          this.formData.description.en = parsedDescription.en;

          this.formData.isActive = !!data.is_active;
          this.formData.start_date = this.formatForDateTimeLocal(data.start_date);
          this.formData.end_date = this.formatForDateTimeLocal(data.end_date);
          this.formData.tracking = data.tracking || null;

          this.existingFiles.image_url = data.image_url || null;
          this.existingFiles.icon_url = data.icon_url || null;
          this.existingFiles.cover_url = data.cover_url || null;

          if (data.image_url) {
            this.showOptionalMedia = true;
            this.$nextTick(() => {
              if (this.$refs.imageUrlRef) {
                this.$refs.imageUrlRef.manuallyAddFile(
                  { size: 300, name: "Image", type: "image/*" },
                  data.image_url
                );
              }
            });
          }

          if (data.cover_url) {
            this.showOptionalMedia = true;
            this.$nextTick(() => {
              if (this.$refs.coverUrlRef) {
                this.$refs.coverUrlRef.manuallyAddFile(
                  { size: 600, name: "Cover", type: "image/*" },
                  data.cover_url
                );
              }
            });
          }

          if (data.icon_url) {
            this.$nextTick(() => {
              if (this.$refs.iconUrlRef) {
                this.$refs.iconUrlRef.manuallyAddFile(
                  { size: 100, name: "Icon", type: "image/*" },
                  data.icon_url
                );
              }
            });
          }

          if (data.products && Array.isArray(data.products)) {
            this.selectedProducts = [...data.products];
            this.formData.products = data.products.map(product => product.id);
          }

          this.loading = false;
        })
        .catch(error => {
          this.loaderError = true;
          console.error(error);
          this.loading = false;
        });

      return;
    }

    this.loading = false;
  },
  methods: {
    parseJsonField(value, fallback) {
      if (!value) return fallback;

      try {
        return typeof value === "string" ? JSON.parse(value) : value;
      } catch (e) {
        return fallback;
      }
    },

    formatForDateTimeLocal(value) {
      if (!value) return null;

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;

      const pad = (n) => String(n).padStart(2, "0");

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },

    getNewFile(refName) {
      const ref = this.$refs[refName];
      if (!ref) return null;

      const acceptedFiles = ref.getAcceptedFiles ? ref.getAcceptedFiles() : [];
      if (!acceptedFiles.length) return null;

      const firstFile = acceptedFiles[0];

      // Dacă fișierul a fost adăugat manual din URL, nu e File real pentru upload nou
      if (firstFile && firstFile.name && !(firstFile instanceof File)) {
        return null;
      }

      return firstFile instanceof File ? firstFile : null;
    },

    hasExistingOrNewIcon() {
      const newIcon = this.getNewFile("iconUrlRef");
      return !!(newIcon || this.existingFiles.icon_url);
    },

    openProductModal() {
      this.showProductModal = true;
    },

    onProductSelected(products) {
      if (!Array.isArray(products) || !products.length) {
        this.showProductModal = false;
        return;
      }

      products.forEach((product) => {
        if (!product || !product.id) return;

        const exists = this.selectedProducts.some(item => item.id === product.id);

        if (!exists) {
          this.selectedProducts.push(product);
        }
      });

      this.formData.products = this.selectedProducts.map(item => item.id);
      this.errors.products = null;
      this.showProductModal = false;
    },

    removeProduct(index) {
      this.selectedProducts.splice(index, 1);
      this.formData.products = this.selectedProducts.map(item => item.id);

      this.errors.products = this.selectedProducts.length
        ? null
        : "Trebuie să atașezi cel puțin un produs.";
    },

    onProductsReorder() {
      this.formData.products = this.selectedProducts.map(item => item.id);
      this.errors.products = this.selectedProducts.length
        ? null
        : "Trebuie să atașezi cel puțin un produs.";
    },

    getProductLabel(product) {
      return product.name || product.title || product.label || product.code || "Produs fără nume";
    },

    goBack() {
      window.location.href = "/multibeneficii/categories-up-mobil-plus";
    },

    onDeleteCategory() {
      // dacă ai alt flow de delete, înlocuiești aici
      const confirmed = window.confirm("Sigur vrei să ștergi această categorie?");
      if (!confirmed) return;

      this.loading = true;

      if (HttpCatServices.delete && this.itemId) {
        HttpCatServices.delete(this.itemId)
          .then(() => {
            this.goBack();
          })
          .catch(error => {
            this.loaderError = true;
            console.error(error);
            this.loading = false;
          });

        return;
      }

      this.loading = false;
    },

    validateForm() {
      const errors = {
        title: {},
        description: {},
        start_date: null,
        end_date: null,
        tracking: null,
        products: null,
        icon_url: null
      };

      if (!this.formData.start_date) {
        errors.start_date = "Start date is required.";
      }

      if (!this.formData.end_date) {
        errors.end_date = "End date is required.";
      }

      if (
        this.formData.start_date &&
        this.formData.end_date &&
        new Date(this.formData.end_date) < new Date(this.formData.start_date)
      ) {
        errors.end_date = "End date must be greater than or equal to start date.";
      }

      if (!this.formData.tracking) {
        errors.tracking = "Tracking is required.";
      } else if (this.formData.tracking.length > 255) {
        errors.tracking = "Tracking must be less than 255 characters.";
      }

      if (!this.formData.title.ro) {
        errors.title.ro = "Title (Ro) is required.";
      } else if (this.formData.title.ro.length > 250) {
        errors.title.ro = "Title (Ro) must be less than 250 characters.";
      }

      if (!this.formData.title.en) {
        errors.title.en = "Title (En) is required.";
      } else if (this.formData.title.en.length > 250) {
        errors.title.en = "Title (En) must be less than 250 characters.";
      }

      if (!this.formData.description.ro) {
        errors.description.ro = "Description (Ro) is required.";
      } else if (this.formData.description.ro.length > 1500) {
        errors.description.ro = "Description (Ro) must be less than 1500 characters.";
      }

      if (!this.formData.description.en) {
        errors.description.en = "Description (En) is required.";
      } else if (this.formData.description.en.length > 1500) {
        errors.description.en = "Description (En) must be less than 1500 characters.";
      }

      if (!this.selectedProducts.length) {
        errors.products = "Trebuie să atașezi cel puțin un produs.";
      }

      if (!this.hasExistingOrNewIcon()) {
        errors.icon_url = "Iconița este obligatorie.";
      }

      const hasTitleErrors = Object.keys(errors.title).length > 0;
      const hasDescriptionErrors = Object.keys(errors.description).length > 0;
      const hasStartDateError = !!errors.start_date;
      const hasEndDateError = !!errors.end_date;
      const hasTrackingError = !!errors.tracking;
      const hasProductsError = !!errors.products;
      const hasIconError = !!errors.icon_url;

      if (
        !hasTitleErrors &&
        !hasDescriptionErrors &&
        !hasStartDateError &&
        !hasEndDateError &&
        !hasTrackingError &&
        !hasProductsError &&
        !hasIconError
      ) {
        this.errors = {
          title: { ro: null, en: null },
          description: { ro: null, en: null },
          start_date: null,
          end_date: null,
          tracking: null,
          products: null,
          icon_url: null
        };
        return true;
      }

      this.errors = errors;
      return false;
    },

    fillBackendErrors(serverErrors) {
      if (!serverErrors) return;

      Object.keys(serverErrors).forEach((key) => {
        if (key.includes("title.ro")) this.errors.title.ro = serverErrors[key][0];
        else if (key.includes("title.en")) this.errors.title.en = serverErrors[key][0];
        else if (key.includes("description.ro")) this.errors.description.ro = serverErrors[key][0];
        else if (key.includes("description.en")) this.errors.description.en = serverErrors[key][0];
        else if (Object.prototype.hasOwnProperty.call(this.errors, key)) {
          this.errors[key] = serverErrors[key][0];
        }
      });
    },

    submitForm() {
      if (!this.validateForm()) return;

      const formData = new FormData();

      formData.set("name", this.formData.title.en.replace(/:/g, "_"));
      formData.append("title", JSON.stringify({
        ro: this.formData.title.ro,
        en: this.formData.title.en
      }));
      formData.append("description", JSON.stringify({
        ro: this.formData.description.ro,
        en: this.formData.description.en
      }));
      formData.append("is_active", this.formData.isActive ? "1" : "0");
      formData.append("start_date", this.formData.start_date);
      formData.append("end_date", this.formData.end_date);
      formData.append("tracking", this.formData.tracking);

      this.selectedProducts.forEach((product, index) => {
        formData.append(`products[${index}][id]`, product.id);
        formData.append(`products[${index}][position]`, index + 1);
      });

      const iconFile = this.getNewFile("iconUrlRef");
      const imageFile = this.getNewFile("imageUrlRef");
      const coverFile = this.getNewFile("coverUrlRef");

      if (iconFile) {
        formData.append("icon_url", iconFile);
      }

      if (imageFile) {
        formData.append("image_url", imageFile);
      }

      if (coverFile) {
        formData.append("cover_url", coverFile);
      }

      this.loading = true;
      this.loaderError = false;

      const request = this.formAction === "update"
        ? HttpCatServices.update(formData, this.itemId)
        : HttpCatServices.create(formData);

      request
        .then(() => {
          this.goBack();
        })
        .catch(error => {
          this.loaderError = true;

          if (error.response && error.response.data && error.response.data.errors) {
            this.fillBackendErrors(error.response.data.errors);
          }

          console.error(error);
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss">
.category-item-crud {
  .form-group {
    margin-bottom: 1rem;

    .label-input {
      font-weight: 700;
      font-size: .85rem;
      color: #2d3748;
    }
  }

  .form-control,
  .form-control-textarea {
    border: 1px solid #d9e2ec;
    border-radius: 10px;
    padding: 10px 12px;
    transition: border-color .2s ease, box-shadow .2s ease;
    background: #fff;
  }

  .form-control:focus,
  .form-control-textarea:focus {
    outline: none;
    border-color: #8aa4ff;
    box-shadow: 0 0 0 3px rgba(138, 164, 255, 0.15);
  }

  .upload-card {
    background: #fff;
    border: 1px solid #e7edf3;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.06);
    overflow: hidden;
  }

  .primary-upload-card {
    border: 1px solid #d8e4ff;
    background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
  }

  .optional-upload-card {
    border-style: dashed;
  }

  .upload-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px;
    border-bottom: 1px solid #edf2f7;
  }

  .upload-card-body {
    padding: 18px;
  }

  .upload-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1f2937;
  }

  .upload-subtitle {
    font-size: .82rem;
    color: #6b7280;
  }

  .required-badge {
    background: #eef4ff;
    color: #3559e0;
    border: 1px solid #d8e4ff;
    font-size: .72rem;
    font-weight: 700;
    padding: 6px 10px;
    border-radius: 999px;
  }

  .optional-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: none;
    background: transparent;
    padding: 16px 18px;
    font-weight: 700;
    color: #1f2937;
    cursor: pointer;
  }

  .optional-media-block + .optional-media-block {
    border-top: 1px solid #edf2f7;
    padding-top: 1rem;
  }

  .products-section {
    background: #fafcff;
    border: 1px solid #e7edf3;
    border-radius: 16px;
    padding: 18px;
  }

  .products-table {
    margin-bottom: 0;
    background: #fff;

    th {
      background: #f8fafc;
      font-size: .85rem;
      color: #475467;
    }

    td,
    th {
      vertical-align: middle;
    }
  }

  .drag-handle {
    cursor: move;
    font-size: 18px;
    user-select: none;
    display: inline-block;
    color: #667085;
  }

  .sortable-ghost {
    opacity: .5;
    background: #f5f7fb !important;
  }

  .dropzone {
    border: none !important;
    min-height: auto !important;
    padding: 0 !important;
    background: transparent !important;
  }

  .vue-dropzone {
    display: flex;
    justify-content: center;
    border: 2px dashed #cfd8e3 !important;
    border-radius: 14px !important;
    background: #fbfdff !important;
    padding: 10px !important;
    min-height: 140px;
    transition: border-color .2s ease, background .2s ease;
  }

  .vue-dropzone:hover {
    border-color: #97a6ba !important;
    background: #f7fbff !important;
  }

  #iconUrl.vue-dropzone {
    min-height: 160px;
    background: linear-gradient(180deg, #f8fbff 0%, #fdfefe 100%) !important;
  }

  .vue-dropzone .dz-message {
    margin: auto !important;
    color: #667085;
    font-weight: 600;
    font-size: .9rem;
    text-align: center;
  }

  .vue-dropzone > .dz-preview {
    margin: 10px;
  }

  .vue-dropzone > .dz-preview .dz-remove {
    font-size: .72rem;
    margin-top: 8px;
    color: #c0392b;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity .2s ease;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
